/**
 * FctBatchProgress — indicador inline de procesamiento por lotes.
 *
 * Patrón GCvsc §7.1: aparece encima de la tabla mientras se procesa una
 * lista de elementos uno a uno (transmisiones, empleados, etc.). El bucle
 * de procesamiento lo gestiona el consumidor; este componente sólo pinta el
 * estado.
 *
 * Características:
 *  - Inline (no modal). No bloquea la UI.
 *  - Cambia de color según el estado final (azul en curso, verde OK, naranja
 *    si hubo errores).
 *  - Barra de progreso con `status` adaptativo de AntD.
 *  - Botón "Cancelar" durante el proceso, "Cerrar" al terminar.
 *
 * Cancelación cooperativa: el consumidor mantiene un `useRef<boolean>` y lo
 * comprueba en cada iteración del bucle. Este componente sólo dispara
 * `onCancel`; es responsabilidad del consumidor ponerlo a `true`.
 *
 * Uso:
 *
 *   const [progress, setProgress] = useState<FctBatchProgressState | null>(null);
 *   const cancelRef = useRef(false);
 *
 *   const handleProcessAll = async (items: Item[]) => {
 *     cancelRef.current = false;
 *     setProgress({ total: items.length, current: 0, currentName: '',
 *                   success: 0, failed: 0, cancelled: false, done: false });
 *
 *     for (let i = 0; i < items.length; i++) {
 *       if (cancelRef.current) {
 *         setProgress(p => p && { ...p, cancelled: true, done: true });
 *         return;
 *       }
 *       setProgress(p => p && { ...p, current: i + 1, currentName: items[i].name });
 *       try {
 *         await processOne(items[i]);
 *         setProgress(p => p && { ...p, success: p.success + 1 });
 *       } catch {
 *         setProgress(p => p && { ...p, failed: p.failed + 1 });
 *       }
 *     }
 *     setProgress(p => p && { ...p, done: true });
 *   };
 *
 *   return (
 *     <>
 *       {progress && (
 *         <FctBatchProgress
 *           progress={progress}
 *           itemLabel="transmisiones"
 *           onCancel={() => { cancelRef.current = true; }}
 *           onClose={() => setProgress(null)}
 *         />
 *       )}
 *       <Table ... />
 *     </>
 *   );
 */

import React from 'react';
import { Progress, Spin, Space, Typography, Button } from 'antd';
import { StopOutlined } from '@ant-design/icons';
import { fctColors } from '../../theme/theme';

const { Text } = Typography;

export interface FctBatchProgressState {
  /** Total de elementos a procesar. */
  total: number;
  /** Índice del elemento actual (1-based mientras está en curso, igual a total al terminar). */
  current: number;
  /** Nombre del elemento actual (para mostrar en la cabecera mientras se procesa). */
  currentName: string;
  /** Elementos procesados correctamente. */
  success: number;
  /** Elementos con error. */
  failed: number;
  /** True si el usuario canceló el proceso. */
  cancelled: boolean;
  /** True cuando el bucle ha terminado (por finalización o cancelación). */
  done: boolean;
}

interface FctBatchProgressProps {
  progress: FctBatchProgressState;
  /** Nombre del tipo de elemento procesado, en plural (ej. "transmisiones"). */
  itemLabel?: string;
  /** Callback al hacer clic en Cancelar (durante el proceso). */
  onCancel?: () => void;
  /** Callback al hacer clic en Cerrar (al terminar). */
  onClose?: () => void;
}

export const FctBatchProgress: React.FC<FctBatchProgressProps> = ({
  progress,
  itemLabel = 'elementos',
  onCancel,
  onClose,
}) => {
  const hasErrors = progress.failed > 0;

  // Selección de colores según estado
  const background = progress.done
    ? hasErrors
      ? fctColors.progresoErrorFondo
      : fctColors.progresoOkFondo
    : fctColors.progresoActivoFondo;

  const borderColor = progress.done
    ? hasErrors
      ? fctColors.progresoErrorBorde
      : fctColors.progresoOkBorde
    : fctColors.progresoActivoBorde;

  // Status de la barra de Progress
  const barStatus: 'active' | 'success' | 'exception' = progress.done
    ? hasErrors
      ? 'exception'
      : 'success'
    : 'active';

  // Texto principal
  const headerText = progress.done
    ? progress.cancelled
      ? `Procesamiento cancelado — ${progress.current - 1} de ${progress.total}`
      : `Procesamiento completado — ${progress.total} ${itemLabel}`
    : `Procesando ${Math.ceil(progress.current)} de ${progress.total}...`;

  const percent =
    progress.total > 0
      ? Math.round((progress.current / progress.total) * 100)
      : 0;

  return (
    <div
      style={{
        marginBottom: 12,
        padding: '12px 16px',
        background,
        border: `1px solid ${borderColor}`,
        borderRadius: 6,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
        }}
      >
        <Space>
          {!progress.done && <Spin size="small" />}
          <Text strong>{headerText}</Text>
        </Space>
        {progress.done ? (
          onClose && (
            <Button size="small" onClick={onClose}>
              Cerrar
            </Button>
          )
        ) : (
          onCancel && (
            <Button
              size="small"
              icon={<StopOutlined />}
              onClick={onCancel}
              danger
            >
              Cancelar
            </Button>
          )
        )}
      </div>

      <Progress percent={percent} status={barStatus} size="small" />

      <div style={{ marginTop: 4, display: 'flex', gap: 16 }}>
        {!progress.done && progress.currentName && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {progress.currentName}
          </Text>
        )}
        <Text type="success" style={{ fontSize: 12 }}>
          {progress.success} OK
        </Text>
        {progress.failed > 0 && (
          <Text type="danger" style={{ fontSize: 12 }}>
            {progress.failed} con error
          </Text>
        )}
      </div>
    </div>
  );
};
